// The console frame: a top bar (mark, project switcher, ⌘K, docs, account menu) and, inside a
// project or the admin area, a left rail of products in Firebase's groups with pinned shortcuts.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { auth } from "../api.ts";
import { useConsole } from "../App.tsx";
import { projectHref, type Route } from "../router.ts";
import { ADMIN_PAGES, ICONS, PRODUCTS, productById, projectColor } from "./nav.tsx";
import { backendHost } from "../lib/kit.ts";
import { Palette } from "./Palette.tsx";
import { useShortcuts } from "./shortcuts.ts";

function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && close();
    const k = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("keydown", k);
    };
  }, [open, close]);
  return ref;
}

function Switcher({ route }: { route: Route }) {
  const { projects } = useConsole();
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const cur = projects?.find((p) => p.id === route.id);
  const label = route.name === "admin" ? "Admin" : cur ? cur.name : "All projects";
  return (
    <div className="switcher" ref={ref}>
      <button className="switcher-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu">
        {cur && <span className="dot" style={{ background: projectColor(cur.id, projects!) }} />}
        <span className="name">{label}</span>
        <span className="caret">{ICONS.caret}</span>
      </button>
      {open && (
        <div className="menu" role="menu" onClick={() => setOpen(false)}>
          <a href="#/" className={route.name === "home" ? "on" : ""}>{ICONS.projects} All projects</a>
          {projects && projects.length > 0 && <div className="menu-label">Projects</div>}
          {projects?.map((p) => (
            <a key={p.id} href={projectHref(p.id)} className={p.id === route.id ? "on" : ""}>
              <span className="dot" style={{ background: projectColor(p.id, projects) }} />
              {p.name}
              <span className="sub">{p.status === "ready" || p.apiUrl ? "live" : p.status}</span>
            </a>
          ))}
          <hr />
          <a href="#/new">{ICONS.plus} New project</a>
        </div>
      )}
    </div>
  );
}

function AccountMenu() {
  const { user, account, isAdmin } = useConsole();
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const initial = String(account.name || user.email || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="switcher" ref={ref}>
      <button className="avatar" onClick={() => setOpen((o) => !o)} aria-label="Account" aria-expanded={open}>{initial}</button>
      {open && (
        <div className="menu right" role="menu" onClick={() => setOpen(false)}>
          <div className="menu-label">{user.email}</div>
          <a href="#/account">Account settings</a>
          <a href="#/account">Weekly digest</a>
          {isAdmin && <a href="#/admin/projects">Admin</a>}
          <hr />
          <button onClick={() => void auth.signOut()}>Sign out</button>
        </div>
      )}
    </div>
  );
}

function ProjectRail({ route, onNav }: { route: Route; onNav: () => void }) {
  const { projects } = useConsole();
  const p = projects?.find((x) => x.id === route.id);
  const shortcuts = useShortcuts(route.id);
  const firebase = p?.source !== "fresh";
  // Until the switched app has shipped (the kit's cutover step), Firebase projects keep the kit in the
  // rail; fresh projects keep their launch guide until its checklist is ticked.
  const kit = (p?.kit ?? {}) as Record<string, boolean>;
  const migrating = !!p && (firebase ? !kit.cutover_ship : !["rules", "origins", "key"].every((c) => kit[`launch_${c}`]));
  const link = (id: string, label: string, tab?: string) => (
    <a key={`${id}/${tab ?? ""}`} href={projectHref(route.id!, id, tab)} className={route.product === id && (!tab || route.tab === tab) ? "on" : ""} onClick={onNav} title={label}>
      {ICONS[id]}<span className="label">{label}</span>
    </a>
  );
  const group = (g: string, label: string) => (
    <>
      <div className="rail-group">{label}</div>
      {PRODUCTS.filter((x) => x.group === g).map((x) => link(x.id, x.label))}
    </>
  );
  return (
    <>
      <div className="rail-top">
        {link("overview", "Project Overview")}
        <a href={projectHref(route.id!, "settings")} className={`icon-btn${route.product === "settings" ? " on" : ""}`} title="Project settings" onClick={onNav} style={{ width: 34, padding: 0, justifyContent: "center" }}>{ICONS.settings}</a>
      </div>
      {shortcuts.length > 0 && (
        <>
          <div className="rail-group">Shortcuts</div>
          {shortcuts.map((s) => {
            const [prod, tab] = s.split("/");
            const def = productById(prod!);
            const t = def?.tabs.find((x) => x.id === tab);
            return link(prod!, t ? `${def!.label} · ${t.label}` : (def?.label ?? s), tab);
          })}
        </>
      )}
      {migrating && link("migrate", firebase ? "Migrate from Firebase" : "Launch guide")}
      {group("build", "Build")}
      {group("engage", "Engage")}
      {group("monitor", "Monitor")}
      <div className="rail-foot">
        <span>Free beta</span>
        <span className="mono">{p ? backendHost(p) : ""}</span>
      </div>
    </>
  );
}

function AdminRail({ route, onNav }: { route: Route; onNav: () => void }) {
  return (
    <>
      <div className="rail-group">Admin</div>
      {ADMIN_PAGES.map((a) => (
        <a key={a.id} href={`#/admin/${a.id}`} className={route.product === a.id ? "on" : ""} onClick={onNav} title={a.label}>{ICONS[a.icon]}<span className="label">{a.label}</span></a>
      ))}
      <div className="rail-group">Mail</div>
      <a href="#/admin/mail/templates" className={route.product === "mail" && route.tab === "templates" ? "on" : ""} onClick={onNav} title="Templates">{ICONS.templates}<span className="label">Templates</span></a>
    </>
  );
}

export function Shell({ route, children }: { route: Route; children: ReactNode }) {
  const [palette, setPalette] = useState(false);
  const [sheet, setSheet] = useState(false);
  const withRail = (route.name === "project" && !!route.id) || route.name === "admin";
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  useEffect(() => {
    setSheet(false);
    setPalette(false);
    window.scrollTo(0, 0);
  }, [route.name, route.id, route.product, route.tab]);
  return (
    <>
      <header className="top">
        {withRail && <button className="icon-btn rail-toggle" onClick={() => setSheet((s) => !s)} aria-label="Menu">{ICONS.menu}</button>}
        <a className="mark" href="#/" aria-label="Estra console home">
          <svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3l3 10 10 3-10 3-3 10-3-10-10-3 10-3z" /></svg>
          <span>estra</span>
        </a>
        <span className="top-sep" />
        <Switcher route={route} />
        <div className="top-right">
          <button className="search-btn" onClick={() => setPalette(true)}>{ICONS.search}<span className="label">Search or jump to…</span><span className="kbd">⌘K</span></button>
          <a className="icon-btn docs" href="/docs" title="Docs" target="_blank" rel="noreferrer">{ICONS.docs}</a>
          <AccountMenu />
        </div>
      </header>
      <div className={`frame${withRail ? "" : " bare"}`}>
        {withRail && (
          <>
            <nav className={`rail${sheet ? " open" : ""}`} aria-label={route.name === "admin" ? "Admin" : "Products"}>
              {route.name === "admin" ? <AdminRail route={route} onNav={() => setSheet(false)} /> : <ProjectRail route={route} onNav={() => setSheet(false)} />}
            </nav>
            <div className={`scrim${sheet ? " open" : ""}`} onClick={() => setSheet(false)} />
          </>
        )}
        <main className="main">{children}</main>
      </div>
      {palette && <Palette route={route} onClose={() => setPalette(false)} />}
    </>
  );
}
